import {
  isPendingShiftSwap,
  shiftHasConflict,
  shiftHasConflictType,
} from '@/lib/schedule-v2/shiftV2Functions';
import {
  isAssigned,
  isLocked,
  isOpenAndUnassigned,
  isPublished,
} from '@/lib/schedule-v2/shiftV2Status';
import { isFuture, isPresent } from '@/util/dateArithmetic';
import { minutesRemaining } from '@/util/entityFunctions';
import spacetime from 'spacetime';
import {
  OpenShiftResponse,
  OpenShiftResponseStatusEnum,
  Shift,
  ShiftConflictTypeEnum,
  ShiftSwap,
} from '../../../api/v1';

export const canDeleteShift = (shift: Shift) => !shift.lockedAt;

export const canReassignShift = (shift: Shift, timezone: string) =>
  isAssigned(shift) &&
  !shift.lockedAt &&
  isFuture(shift.endsAt) &&
  minutesRemaining(timezone, shift) > 15;

export const canAssignShift = (
  shift: Shift,
  openResponses: OpenShiftResponse[],
) =>
  !isAssigned(shift) &&
  isFuture(shift.startsAt) &&
  !shift.lockedAt &&
  !(
    shift.open &&
    openResponses.length &&
    openResponses.some((r) => r.status === OpenShiftResponseStatusEnum.Pending)
  );

// Replace is copying a shift to another employee
// If the employee has two shifts which conflict then we should only allow reassigning
export const canReplaceShift = (
  shift: Shift,
  timezone: string,
  ignoreShiftConflict = false,
) =>
  isAssigned(shift) &&
  isPublished(shift) &&
  isPresent(shift.startsAt, shift.endsAt) &&
  minutesRemaining(timezone, shift) > 15 &&
  (ignoreShiftConflict ||
    !shiftHasConflictType(shift, ShiftConflictTypeEnum.Shift));

export const canUnassignShift = (shift: Shift) =>
  isAssigned(shift) && !shift.lockedAt && isFuture(shift.endsAt);

export const canSwapShift = (
  shift: Shift,
  loggedInEmployeeId: number,
  shiftSwaps: ShiftSwap[] | [],
): boolean =>
  // Belongs to the logged in employee
  shift.employeeId === loggedInEmployeeId &&
  // Not locked
  !isLocked(shift) &&
  // Shift is published
  isPublished(shift) &&
  // Shift starts in the future
  isFuture(shift.startsAt) &&
  // Not already pending a swap
  !shiftSwaps.find((ss) => ss.shiftId === shift.id && isPendingShiftSwap(ss));

export const canCancelSwapShift = (
  shift: Shift,
  loggedInEmployeeId: number,
  shiftSwaps: ShiftSwap[],
  swapCutOffHours: number,
): boolean =>
  // Belongs to the logged in employee
  shift.employeeId === loggedInEmployeeId &&
  // Published
  isPublished(shift) &&
  // Not locked
  !isLocked(shift) &&
  // Shift starts in the future
  isFuture(
    spacetime(shift.startsAt).subtract(swapCutOffHours, 'hour').toNativeDate(),
  ) &&
  // Has a pending swap
  !!shiftSwaps.find((ss) => ss.shiftId === shift.id && isPendingShiftSwap(ss));

export const canClaimOpenShift = (
  shift: Shift,
  loggedInEmployeeId: number,
  openShiftResponses: OpenShiftResponse[],
): boolean =>
  isOpenAndUnassigned(shift) &&
  isFuture(shift.startsAt) &&
  isPublished(shift) &&
  !openShiftResponses.find(
    (response) =>
      response.shiftId === shift.id &&
      response.employeeId === loggedInEmployeeId &&
      response.status !== OpenShiftResponseStatusEnum.Rejected,
  );

export const canWithdrawOpenShiftRequest = (
  shift: Shift,
  loggedInEmployeeId: number,
  openShiftResponses: OpenShiftResponse[],
): boolean =>
  isOpenAndUnassigned(shift) &&
  isFuture(shift.startsAt) &&
  isPublished(shift) &&
  !!openShiftResponses.find(
    (response) =>
      response.shiftId === shift.id &&
      response.employeeId === loggedInEmployeeId &&
      response.status === OpenShiftResponseStatusEnum.Pending,
  );

export const canPublishShift = (shift: Shift): boolean =>
  !isPublished(shift) &&
  isFuture(shift.startsAt) &&
  (isOpenAndUnassigned(shift) ||
    (isAssigned(shift) && !shiftHasConflict(shift)));
